// SessionPanel.tsx - Main session management component
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { trimTrailingSlash } from "../../lib/url";
import "./SessionPanel.css";

import type { UpdateEvent as NetUpdateEvent } from "../../types/domain";
import { useSession } from "./useSession";
import { SENTINEL, isRealSessionId, todayISO } from "./constants";

import { SummaryCard, SummaryCardHandle } from "./parts/SummaryCard";
import { TranscriptPortal } from "./parts/TranscriptPortal";
import { useReplayState } from "./hooks/useReplayState";
import { AffektraPortal } from "./parts/AffektraPortal";
import NotesPanel from "./parts/NotesPanel";
import type { NoteFormat } from "./hooks/useProgressNotes";
import type {
  AffektraActionCtx,
  AffektraRunStatus,
  AgentCard,
  AffektraCard,
  CardFeedbackCtx,
} from "./parts/AffektraPortal";

import { Toolbar } from "./Toolbar";
import { useAuthedFetch } from "../../hooks/useApi";
import { exportCsv } from "./csv";
import { saveSession } from "./api";
import { usePrefill } from "./hooks/usePrefill";
import { useSummary } from "./hooks/useSummary";
import { useTranscriptPolling } from "./hooks/useTranscriptPolling";
import { useSessionActions, resetSessionState } from "./hooks/useSessionActions";
import { useSessionData } from "./hooks/useSessionData";
import { useSaveSession } from "./hooks/useSaveSession";
import { parseTranscriptToEvents } from "./lib/transcriptParser";
import { markSessionDirty, clearSessionDirty } from "./lib/sessionDirty";
import { useAuth } from "../../auth/AuthProvider";
import { useToast } from "../../components/Toast";
import ConfirmDialog from "../../components/ConfirmDialog";
import SessionContextPanel from "../../components/SessionContextPanel";
import { Button } from "../../components/ui/Button";
import { Layers, Sparkles, Save, Trash2, XCircle } from "lucide-react";
import { Dialog, DialogContent } from "@mui/material";
import { ExperientialPanel } from "../experiential/ExperientialPanel";

export type UpdateEvent = NetUpdateEvent;
type UIEvent = NetUpdateEvent;

const isReal = isRealSessionId;

type SessionPanelProps = {
  apiBaseUrl: string;
  getToken: () => Promise<string>;
  onSummary?: (s: string) => void;
  sentToWorkspaceAt?: number;
  selectedSessionId?: string;
  selectedPatientId?: string;
  prefillSummary?: string;
  prefillEvents?: NetUpdateEvent[];
  onSaved?: (() => void) | ((sid: string) => void);
  onEffectiveSessionId?: (sid?: string) => void;
  affektraOpen?: boolean;
  onAffektraOpenChange?: (open: boolean) => void;
  onRunAffektraAction?: (ctx: AffektraActionCtx) => void;
  affektraCards?: (AgentCard | AffektraCard)[];
  affektraStatus?: AffektraRunStatus;
  affektraError?: string;
  onExecuteCard?: (card: AgentCard) => Promise<string | void>;
  onCardFeedback?: (ctx: CardFeedbackCtx) => void;
  /** Therapist's selected summarize prompt (picked in the prompt library).
   * note_format present → the Notes tab is a structured SOAP/DAP/BIRP note;
   * absent/null → the freeform summary. There is no picker here. */
  summarizePrompt?: { id: string; note_format?: NoteFormat } | null;
  /** Deep-link target from a matched moment: opens the timeline tab and
   * scrolls/highlights the matching turn(s) in TranscriptPortal. */
  highlightRange?: { startMs: number; endMs: number } | null;
};

type BottomTab = "timeline" | "notes" | "insights";

function pickTherapistId(user: any | undefined): string | undefined {
  if (!user) return undefined;
  return user.sub || user?.attributes?.sub || user?.signInUserSession?.idToken?.payload?.sub || user.username || undefined;
}

export default function SessionPanel({
  apiBaseUrl,
  getToken,
  onSummary,
  sentToWorkspaceAt,
  selectedSessionId,
  selectedPatientId,
  prefillSummary,
  prefillEvents,
  onSaved,
  onEffectiveSessionId,
  affektraOpen,
  onAffektraOpenChange,
  onRunAffektraAction,
  affektraCards,
  affektraStatus,
  affektraError,
  onExecuteCard,
  onCardFeedback,
  summarizePrompt,
  highlightRange,
}: SessionPanelProps) {
  const { t, i18n } = useTranslation();
  const API = trimTrailingSlash(apiBaseUrl || "");

  // UI state
  const [showTranscript, setShowTranscript] = useState(false);
  const [bottomTab, setBottomTab] = useState<BottomTab>("notes");
  // Notes tab mode follows the selected summarize prompt — no picker here.
  const noteFormat = summarizePrompt?.note_format;
  const [experientialOpen, setExperientialOpen] = useState(false);

  // Hooks drive showTranscript (recording/upload/replay open it, reset closes it);
  // mirror that into the tab model so legacy behaviour is preserved.
  useEffect(() => {
    setBottomTab((prev) => {
      if (showTranscript) return prev === "insights" ? prev : "timeline";
      return prev === "timeline" ? "notes" : prev;
    });
  }, [showTranscript]);

  // Header Affektra button (and persisted preference) selects the Insights tab.
  useEffect(() => {
    if (affektraOpen === undefined) return;
    setBottomTab((prev) => {
      if (affektraOpen) return "insights";
      return prev === "insights" ? (showTranscript ? "timeline" : "notes") : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affektraOpen]);

  const selectTab = useCallback((tab: BottomTab) => {
    setBottomTab(tab);
    onAffektraOpenChange?.(tab === "insights");
  }, [onAffektraOpenChange]);

  // A deep-linked highlightRange (from a matched moment) opens the timeline
  // tab so the highlighted turn(s) are visible. Only fires on the transition
  // to a non-null value; later user actions may switch tabs again.
  const highlightRangeKey = highlightRange ? `${highlightRange.startMs}-${highlightRange.endMs}` : null;
  useEffect(() => {
    if (highlightRangeKey) selectTab("timeline");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightRangeKey]);
  const [pinnedSeqs, setPinnedSeqs] = useState<Set<number>>(new Set());
  const [justSent, setJustSent] = useState(false);
  const [showContextSheet, setShowContextSheet] = useState(false);
  const [transcriptMode, setTranscriptMode] = useState<"history" | "live" | "replay">("history");
  const [liveTranscript, setLiveTranscript] = useState<string>("");
  const [confirmClearTranscript, setConfirmClearTranscript] = useState(false);
  const [confirmDeleteSession, setConfirmDeleteSession] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{
    message: string;
    type: "info" | "success" | "error";
    action?: { label: string; onClick: () => void };
  } | null>(null);
  const toast = useToast();

  // Replay state
  const {
    state: replayState,
    enterReplay,
    exitReplay,
    play: replayPlay,
    pause: replayPause,
    seek: replaySeek,
    setSpeed: replaySetSpeed,
    visibleEvents: replayVisibleEvents,
    allEvents: replayAllEvents,
  } = useReplayState();

  // Track revealed chunk indices for Affektra triggering
  const prevRevealedChunkRef = useRef<number>(-1);

  // Session metadata state
  const [sessionDate, setSessionDate] = useState<string>(() => todayISO());
  const [similarSessionIds, setSimilarSessionIds] = useState<string[]>([]);
  const [assessmentIds, setAssessmentIds] = useState<string[]>([]);
  const [extraNotes, setExtraNotes] = useState<string>("");
  const [sessionLang, setSessionLang] = useState<"en" | "hu" | undefined>(undefined);

  // Refs
  const panelRef = useRef<HTMLDivElement | null>(null);
  const summaryRef = useRef<SummaryCardHandle | null>(null);
  const latestSeqRef = useRef<number>(0);
  const lastPublishedSidRef = useRef<string | undefined>(undefined);

  // Auth and fetch
  const authedFetch = useAuthedFetch();
  const { user, ensureToken } = useAuth();
  const therapistId = pickTherapistId(user);

  // Flash effect for "sent to workspace"
  useEffect(() => {
    if (!sentToWorkspaceAt) return;
    setJustSent(true);
    const t = setTimeout(() => setJustSent(false), 1200);
    return () => clearTimeout(t);
  }, [sentToWorkspaceAt]);

  // Core session hook
  const {
    sessionId, setSessionId, recording, setRecording, events, setEvents,
    latestSeq, setLatestSeq, busy, setBusy, setError, afterSeqRef, seenSeqsRef,
    ensureSession, mintNewSessionId,
  } = useSession({
    onClear: () => {
      setPinnedSeqs(new Set());
      setSummary("");
      setSimilarSessionIds([]);
      setAssessmentIds([]);
      setExtraNotes("");
      setSessionDate(todayISO());
      setSessionLang(undefined);
    },
  });

  useEffect(() => { latestSeqRef.current = typeof latestSeq === "number" ? latestSeq : 0; }, [latestSeq]);

  // Clear upload status when transcript events arrive (only upgrade an in-progress status,
  // never overwrite a terminal error with "complete")
  useEffect(() => {
    if (uploadStatus && uploadStatus.type === "info" && events.length > 0) {
      setUploadStatus({ message: t("session.processingComplete"), type: "success" });
      const timer = setTimeout(() => setUploadStatus(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [events.length, uploadStatus, t]);

  const wantsCurrentSelected = !selectedSessionId || selectedSessionId === SENTINEL;

  // Sync selected session ID
  useEffect(() => {
    if (isReal(selectedSessionId) && selectedSessionId !== sessionId) {
      setSessionId?.(selectedSessionId!);
    }
  }, [selectedSessionId, sessionId, setSessionId]);

  const effectiveSid = useMemo(() =>
    isReal(selectedSessionId) ? selectedSessionId as string :
    isReal(sessionId) ? sessionId as string : undefined,
  [selectedSessionId, sessionId]);

  // Publish effective session ID
  useEffect(() => {
    if (!onEffectiveSessionId) return;
    if (lastPublishedSidRef.current === effectiveSid) return;
    lastPublishedSidRef.current = effectiveSid;
    onEffectiveSessionId(effectiveSid);
  }, [effectiveSid, onEffectiveSessionId]);

  const bubbles = useMemo<UIEvent[]>(() =>
    (events as UIEvent[]).filter((e) => (e.text ?? "").trim().length > 0), [events]);

  // Summary hook — runs the selected summarize prompt (freeform only; the
  // structured-note formats go through NotesPanel/the notes worker instead).
  const { summary, setSummary, summarize } = useSummary({
    API_BASE: API,
    getToken: ensureToken,
    ensureSession,
    sessionId: effectiveSid,
    bubbles,
    pinnedSeqs,
    includePinnedInSummary: true,
    promptId: !noteFormat ? summarizePrompt?.id : undefined,
    onSummary,
    setError,
    setBusy,
  });

  // Reset state when switching to "current" session
  const prevWantsCurrentRef = useRef<boolean>(false);
  useEffect(() => {
    const prev = prevWantsCurrentRef.current;
    prevWantsCurrentRef.current = wantsCurrentSelected;

    if (!prev && wantsCurrentSelected) {
      resetSessionState({
        setSummary, setEvents, setLatestSeq, setPinnedSeqs, setRecording,
        setSessionDate, setSimilarSessionIds, setAssessmentIds, setExtraNotes,
        setLang: setSessionLang,
        setSessionId, setShowTranscript, setTranscriptMode, setLiveTranscript,
        afterSeqRef, seenSeqsRef, latestSeqRef, summaryRef, onEffectiveSessionId,
        lastPublishedSidRef,
      });
    }
  }, [wantsCurrentSelected, setSessionId, setSummary, setEvents, setLatestSeq, setRecording,
      afterSeqRef, seenSeqsRef, onEffectiveSessionId]);

  // Load session data
  useSessionData({
    API, effectiveSid, authedFetch, setEvents, setLatestSeq, setSummary,
    setSessionDate, setPinnedSeqs, setSimilarSessionIds, setAssessmentIds,
    setExtraNotes, setLang: setSessionLang, latestSeqRef, summaryRef,
  });

  // Prefill from props
  usePrefill({
    selectedSessionId, prefillSummary, prefillEvents, summary, setSummary,
    onSummary, events: events as any, setEvents: setEvents as any,
    seenSeqsRef, afterSeqRef, setLatestSeq,
  });

  // Transcript polling
  const { startTranscriptPolling, stopTranscriptPolling } = useTranscriptPolling({
    API, effectiveSid, ensureSession, getToken, setEvents, setLatestSeq,
    setSessionId, onEffectiveSessionId, setLiveTranscript, latestSeqRef,
    lastPublishedSidRef,
  });

  // Stop polling whenever the active session changes (prevents stale data from old session)
  useEffect(() => {
    stopTranscriptPolling();
  }, [effectiveSid, stopTranscriptPolling]);

  // Session actions (clear, delete)
  const { clearTranscript, clearingTranscript, deleteSession } = useSessionActions({
    API, effectiveSid, getToken, setError, setBusy, stopTranscriptPolling,
    setSummary, setEvents, setLatestSeq, setPinnedSeqs, setRecording,
    setSessionDate, setSimilarSessionIds, setAssessmentIds, setExtraNotes,
    setLang: setSessionLang,
    setSessionId, setShowTranscript, setTranscriptMode, setLiveTranscript,
    afterSeqRef, seenSeqsRef, latestSeqRef, summaryRef, onEffectiveSessionId,
    lastPublishedSidRef,
  });

  // Save session
  const { saveTranscript, submitJson } = useSaveSession({
    API, effectiveSid, mintNewSessionId, selectedSessionId, selectedPatientId,
    therapistId, authedFetch, bubbles, summary, sessionDate, similarSessionIds,
    assessmentIds, extraNotes, setBusy, setError, setSummary, setSessionId,
    setSessionDate, setEvents, setLatestSeq, setShowTranscript, setTranscriptMode,
    onEffectiveSessionId,
    onSaved: (sid: string) => { clearSessionDirty(); onSaved?.(sid); },
    summaryRef, latestSeqRef,
  });

  // A freshly loaded/reset session starts clean.
  useEffect(() => { clearSessionDirty(); }, [effectiveSid]);

  // Auto-create session before recording/upload if it doesn't exist
  const ensureSessionBeforeRecording = useCallback(async (): Promise<string> => {
    // If we already have a session, return it
    if (effectiveSid) {
      console.log("[SessionPanel] ensureSessionBeforeRecording: already have session", effectiveSid);
      return effectiveSid;
    }

    // Create a new session
    console.log("[SessionPanel] ensureSessionBeforeRecording: creating new session…");
    if (!API) throw new Error("Backend URL is not configured.");
    if (!therapistId) throw new Error("Missing therapist id.");

    const newSid = mintNewSessionId();
    const body = {
      patient_id: selectedPatientId ?? null,
      therapist_id: therapistId,
      transcript: "",
      summary: "",
      lang: "en",
      session_date: sessionDate || null,
      title: sessionDate || null,
    };

    await saveSession(authedFetch, API, newSid, body);

    // Update state
    setSessionId?.(newSid);
    onEffectiveSessionId?.(newSid);

    console.log("[SessionPanel] ensureSessionBeforeRecording: created session", newSid);
    return newSid;
  }, [effectiveSid, API, therapistId, mintNewSessionId, selectedPatientId, sessionDate, authedFetch, setSessionId, onEffectiveSessionId]);

  // The process_audio lambda caps at 15 min; if nothing has arrived by then the job is dead.
  const PROCESSING_STALL_MS = 16 * 60 * 1000;

  // Kicks off backend audio processing and polls for the transcript, surfacing terminal
  // failures with a retry action. Kept separate from submitVoice so a failed job can be
  // retried without re-uploading the file.
  const startAudioProcessingRef = useRef<((sid: string, s3Key: string, contentType: string) => Promise<void>) | undefined>(undefined);
  const startAudioProcessing = useCallback(async (sid: string, s3Key: string, contentType: string) => {
    const token = await getToken();

    const failProcessing = (message: string, retry: () => void) => {
      stopTranscriptPolling();
      setUploadStatus({ message, type: "error", action: { label: t("session.retry"), onClick: retry } });
    };

    const retryJob = () => { void startAudioProcessingRef.current?.(sid, s3Key, contentType); };

    const beginPolling = () => {
      void startTranscriptPolling(sid, {
        stallTimeoutMs: PROCESSING_STALL_MS,
        onStallTimeout: () => failProcessing(t("session.processingTimeout"), retryJob),
        onPollFailure: () =>
          failProcessing(t("session.transcriptConnectionLost"), () => {
            setUploadStatus({ message: t("session.processingAudio"), type: "info" });
            beginPolling();
          }),
      });
    };

    // Fire off processing without awaiting completion — it can take minutes and the API
    // gateway times out long before the lambda finishes, so an aborted request is expected
    // even on success. A quick non-2xx response, however, is a real failure: surface it.
    fetch(`${API}/sessions/process-audio`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        session_id: sid,
        patient_id: selectedPatientId || "",
        s3_key: s3Key,
        content_type: contentType,
      }),
    }).then(async (resp) => {
      if (!resp.ok) {
        const body = await resp.text().catch(() => "");
        console.error("[SessionPanel] process-audio failed:", resp.status, body);
        failProcessing(`${t("session.processingFailed")} (HTTP ${resp.status})`, retryJob);
      }
    }).catch((err) => {
      console.warn("[SessionPanel] process-audio request ended early (job continues):", err);
    });

    setUploadStatus({ message: t("session.processingAudio"), type: "info" });
    setShowTranscript(true);
    setTranscriptMode("live");
    beginPolling();
  }, [API, getToken, selectedPatientId, startTranscriptPolling, stopTranscriptPolling, t, PROCESSING_STALL_MS]);
  useEffect(() => { startAudioProcessingRef.current = startAudioProcessing; }, [startAudioProcessing]);

  // Callbacks
  const submitVoice = useCallback(async (file?: File) => {
    if (!file) return;

    try {
      setBusy(true);
      setUploadStatus({ message: t("session.uploadingFile"), type: "info" });

      // Ensure we have a session
      let sid = effectiveSid;
      if (!sid) {
        sid = await ensureSessionBeforeRecording();
      }

      if (!sid) {
        throw new Error("Failed to create session for upload");
      }

      const token = await getToken();

      // Step 1: Get pre-signed S3 upload URL
      const urlResp = await fetch(`${API}/sessions/upload-url`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ session_id: sid }),
      });

      const urlResult = await urlResp.json();
      if (!urlResp.ok) {
        throw new Error(urlResult.error || "Failed to get upload URL");
      }

      const { upload_url, s3_key } = urlResult;

      // Step 2: Upload file directly to S3
      const s3Resp = await fetch(upload_url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/octet-stream",
        },
        body: file,
      });

      if (!s3Resp.ok) {
        throw new Error(`S3 upload failed: ${s3Resp.status}`);
      }

      // Step 3: Kick off processing + transcript polling (with failure surfacing and retry)
      await startAudioProcessing(sid, s3_key, file.type || "audio/webm");

    } catch (err: any) {
      console.error("[SessionPanel] Voice upload error:", err);
      setUploadStatus({ message: `${t("session.uploadFailed")}: ${err.message || err}`, type: "error" });
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  }, [effectiveSid, ensureSessionBeforeRecording, getToken, API, setBusy, setError, t, startAudioProcessing]);

  const togglePin = useCallback((seq: number) => {
    setPinnedSeqs((prev) => {
      const next = new Set(prev);
      next.has(seq) ? next.delete(seq) : next.add(seq);
      return next;
    });
  }, []);

  const onExportCsvCb = useCallback(() => exportCsv(effectiveSid, bubbles as any), [effectiveSid, bubbles]);
  const onToggleTranscript = useCallback(() => setShowTranscript((v) => !v), []);

  const handleStartRecording = useCallback((sessionIdFromToolbar?: string) => {
    // If Toolbar created a session, update our state immediately
    if (sessionIdFromToolbar && !effectiveSid) {
      setSessionId?.(sessionIdFromToolbar);
      onEffectiveSessionId?.(sessionIdFromToolbar);
    }

    setRecording(true);
    setShowTranscript(true);
    setTranscriptMode("live");
    setLiveTranscript("");
    // Pass the sessionId to avoid closure issues. Recording continues even if polling dies —
    // surface the lost connection and offer to reconnect instead of failing silently.
    const sid = sessionIdFromToolbar || effectiveSid;
    const beginLivePolling = () => {
      void startTranscriptPolling(sid, {
        onPollFailure: () =>
          setUploadStatus({
            message: t("session.transcriptConnectionLost"),
            type: "error",
            action: {
              label: t("session.retry"),
              onClick: () => { setUploadStatus(null); beginLivePolling(); },
            },
          }),
      });
    };
    beginLivePolling();
  }, [setRecording, startTranscriptPolling, effectiveSid, setSessionId, onEffectiveSessionId, t]);

  const handleStopRecording = useCallback(() => {
    setRecording(false);
    stopTranscriptPolling();
  }, [setRecording, stopTranscriptPolling]);

  // Replay handlers
  const handleEnterReplay = useCallback(() => {
    const maxEndSec = (events as any[]).reduce((m, e) => typeof e?.end_sec === "number" && e.end_sec > m ? e.end_sec : m, 0);
    console.log("[DBG handleEnterReplay] sid=", effectiveSid, "events.length=", events.length, "maxEndSec=", maxEndSec, "firstEvent=", events[0], "lastEvent=", events[events.length - 1]);
    if (events.length === 0) {
      toast.info("No transcript events to replay");
      return;
    }
    // Clear Affektra outputs before replay
    if (onRunAffektraAction && effectiveSid) {
      onRunAffektraAction({
        actionId: "clear_outputs",
        sessionId: effectiveSid,
        patientId: selectedPatientId,
      });
    }
    // Enter replay mode
    enterReplay(events as UpdateEvent[]);
    setTranscriptMode("replay");
    setShowTranscript(true);
    prevRevealedChunkRef.current = -1;
  }, [events, enterReplay, effectiveSid, selectedPatientId, onRunAffektraAction, toast]);

  const handleExitReplay = useCallback(() => {
    exitReplay();
    setTranscriptMode("history");
    prevRevealedChunkRef.current = -1;
    // Stop the periodic agent interval
    if (onRunAffektraAction) {
      onRunAffektraAction({ actionId: "stop" });
    }
  }, [exitReplay, onRunAffektraAction]);

  // Trigger Affektra progressively during replay based on chunk boundaries
  useEffect(() => {
    if (!replayState.isReplayMode || !replayState.isPlaying) return;
    if (!onRunAffektraAction || !effectiveSid) return;

    // Find chunk boundaries from event timing (events in same chunk share end_sec)
    const chunkEndTimes = new Set<number>();
    for (const e of replayAllEvents) {
      const endSec = (e as any).end_sec;
      if (typeof endSec === "number") {
        // Round to nearest second to group events into chunks
        chunkEndTimes.add(Math.ceil(endSec));
      }
    }
    const sortedBoundaries = Array.from(chunkEndTimes).sort((a, b) => a - b);

    // Find current chunk index based on current time
    let currentChunkIndex = -1;
    for (let i = 0; i < sortedBoundaries.length; i++) {
      if (replayState.currentTime >= sortedBoundaries[i]) {
        currentChunkIndex = i;
      }
    }

    // Trigger Affektra if we've crossed into a new chunk AND enough time has passed
    if (currentChunkIndex > prevRevealedChunkRef.current) {
      prevRevealedChunkRef.current = currentChunkIndex;

      // Find the max DynamoDB chunk_index from currently visible replay events
      let maxVisibleChunkIdx: number | undefined;
      for (const e of replayVisibleEvents) {
        const ci = (e as any).chunk_index;
        if (typeof ci === "number" && (maxVisibleChunkIdx === undefined || ci > maxVisibleChunkIdx)) {
          maxVisibleChunkIdx = ci;
        }
      }

      console.log("[SessionPanel] Replay: triggering Affektra (throttled 60s)", {
        chunkIndex: currentChunkIndex,
        maxVisibleChunkIdx,
        currentTime: replayState.currentTime,
      });
      onRunAffektraAction({
        cardId: "global",
        actionId: "play",
        sessionId: effectiveSid,
        patientId: selectedPatientId,
        maxChunkIndex: maxVisibleChunkIdx,
        replay: true,
      });
    }
  }, [replayState.isReplayMode, replayState.isPlaying, replayState.currentTime, replayAllEvents, effectiveSid, selectedPatientId, onRunAffektraAction]);

  // Whenever the active session changes, exit any in-progress replay so the
  // previous session's duration/events can't bleed into the new session's view.
  // The user can click Replay again on the new session.
  const replaySessionSidRef = useRef<string | undefined>(effectiveSid);
  useEffect(() => {
    if (replaySessionSidRef.current !== effectiveSid) {
      replaySessionSidRef.current = effectiveSid;
      exitReplay();
      prevRevealedChunkRef.current = -1;
    }
  }, [effectiveSid, exitReplay]);

  // Check if session can be replayed (has events)
  const canReplay = useMemo(() => {
    return events.length > 0;
  }, [events]);

  const roleClass = (speaker?: string) =>
    (speaker || "").toLowerCase().includes("therapist") ? "spn-bubble--therapist" : "spn-bubble--client";

  const canDelete = !!effectiveSid;

  return (
    <>
    <div className="spn-root" ref={panelRef}>
      <div className="spn-frame">
        <div className="spn-topbar">
          <span className="spn-topbar-title">{t("session.title")}</span>
        </div>

        {uploadStatus && (
          <div className={`spn-status-bar spn-status-bar--${uploadStatus.type}`}>
            <span className="spn-status-bar__spinner" />
            <span>{uploadStatus.message}</span>
            {uploadStatus.action && (
              <Button size="sm" onClick={uploadStatus.action.onClick}>
                {uploadStatus.action.label}
              </Button>
            )}
          </div>
        )}

        <div className="spn-toolbar-row">
          <Toolbar
            recording={!!recording}
            onStartRecording={handleStartRecording}
            onStopRecording={handleStopRecording}
            onUploadVoice={(f) => submitVoice(f)}
            onExportCsv={onExportCsvCb}
            sessionId={effectiveSid}
            therapistId={therapistId}
            patientId={selectedPatientId}
            onEnsureSession={ensureSessionBeforeRecording}
            sessionDate={sessionDate}
            onDateChange={setSessionDate}
            canReplay={canReplay && !recording}
            onStartReplay={replayState.isReplayMode ? handleExitReplay : handleEnterReplay}
            isReplaying={replayState.isReplayMode}
            showTranscript={showTranscript}
            onToggleTranscript={onToggleTranscript}
            onOpenExperiential={selectedPatientId ? () => setExperientialOpen(true) : undefined}
          />
        </div>

        <div className="spn-middle">
          {/* Flat tab row: Timeline | Notes | Insights */}
          <div className="spn-panel-head spn-panel-head--flat">
            <button
              type="button"
              className={`spn-panel-tab spn-panel-tab--flat ${bottomTab === "timeline" ? "spn-panel-tab--active" : ""}`}
              onClick={() => selectTab("timeline")}
            >
              {t("session.timeline")}
              {bubbles.length > 0 && <span className="spn-tab-count">{bubbles.length}</span>}
            </button>
            <button
              type="button"
              className={`spn-panel-tab spn-panel-tab--flat ${bottomTab === "notes" ? "spn-panel-tab--active" : ""}`}
              onClick={() => selectTab("notes")}
            >
              {t("session.notes")}
            </button>
            <button
              type="button"
              className={`spn-panel-tab spn-panel-tab--flat ${bottomTab === "insights" ? "spn-panel-tab--active" : ""}`}
              onClick={() => selectTab("insights")}
            >
              {t("session.insights")}
              {(affektraCards?.length ?? 0) > 0 && <span className="spn-tab-count">{affektraCards!.length}</span>}
            </button>
            <div className="spn-summary-actions">
              {bottomTab === "notes" && !noteFormat && (
                <>
                  {busy && (
                    <span className="spn-status-running" aria-live="polite">
                      {t("common.summarizing")}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    iconOnly
                    size="sm"
                    onClick={() => setShowContextSheet(true)}
                    title={t("session.additionalContext")}
                    aria-label={t("session.additionalContext")}
                  >
                    <Layers size={16} />
                  </Button>
                  <Button
                    variant="ghost"
                    iconOnly
                    size="sm"
                    onClick={() => summarize()}
                    disabled={busy}
                    title={busy ? t("common.summarizing") : t("session.generateSummary")}
                    aria-label={busy ? t("common.summarizing") : t("session.generateSummary")}
                  >
                    <Sparkles size={16} />
                  </Button>
                </>
              )}
              {bottomTab === "timeline" && (
                <>
                  {transcriptMode === "replay" ? (
                    <span className="spn-replay-indicator">
                      <span className="spn-replay-dot" />
                      {t("session.replay")}
                    </span>
                  ) : (
                    <span className={`spn-recording-indicator ${recording ? "spn-recording-indicator--active" : "spn-recording-indicator--idle"}`}>
                      <span className="spn-recording-dot" />
                      {recording ? t("session.recording") : t("session.idle")}
                    </span>
                  )}
                  {/* 960px contingency (spec-sanctioned): the labeled Clear pill would
                      overflow this flex row at the panel's default 430px width, so it
                      stays an icon square — the tooltip keeps the wording. */}
                  <Button
                    variant="danger"
                    size="sm"
                    iconOnly
                    title={!effectiveSid ? t("session.noSession") : clearingTranscript ? t("common.clearing") : t("session.clearTranscript")}
                    aria-label={t("session.clearTranscript")}
                    onClick={() => setConfirmClearTranscript(true)}
                    disabled={!effectiveSid || clearingTranscript}
                  >
                    <XCircle size={16} />
                  </Button>
                </>
              )}
            </div>
          </div>

          {bottomTab === "notes" && (
            // The Notes tab renders whatever the selected summarize prompt
            // produces: a structured SOAP/DAP/BIRP note (draft/sign) when the
            // prompt is tagged note_format, else the freeform summary. The
            // prompt is picked in the prompt library, not here.
            noteFormat ? (
              <NotesPanel
                format={noteFormat}
                promptId={summarizePrompt?.id}
                sessionId={effectiveSid}
                sessionLang={sessionLang}
                apiBase={API}
                ensureToken={ensureToken}
              />
            ) : (
              <SummaryCard
                ref={summaryRef}
                busy={busy}
                justSent={justSent}
                includePinned={true}
                setIncludePinned={() => {}}
                summary={summary}
                setSummary={(s: string) => { markSessionDirty(); setSummary(s); onSummary?.(s); }}
                onSummarize={() => summarize()}
              />
            )
          )}

          {bottomTab === "timeline" && (
            <TranscriptPortal
              open={true}
              onClose={() => setShowTranscript(false)}
              latestSeq={latestSeq}
              bubbles={transcriptMode === "replay" ? replayVisibleEvents as any : bubbles as any}
              pinnedSeqs={pinnedSeqs}
              togglePin={togglePin}
              roleClass={roleClass}
              mode={transcriptMode}
              liveTranscript={liveTranscript}
              recording={recording}
              replayState={replayState.isReplayMode ? replayState : undefined}
              onPlay={replayPlay}
              onPause={replayPause}
              onSeek={replaySeek}
              onSpeedChange={replaySetSpeed}
              onExitReplay={handleExitReplay}
              highlightRange={highlightRange}
            />
          )}

          {bottomTab === "insights" && (
            <div className="spn-insights">
              <AffektraPortal
                panelRef={panelRef}
                open={true}
                onClose={() => selectTab(showTranscript ? "timeline" : "notes")}
                cards={affektraCards ?? []}
                patientId={selectedPatientId || undefined}
                sessionId={effectiveSid}
                sessionSummary={summary}
                runStatus={affektraStatus}
                errorText={affektraError}
                onRunAction={() => {}}
                onRunActionEx={onRunAffektraAction}
                onExecuteCard={onExecuteCard}
                onCardFeedback={onCardFeedback}
              />
            </div>
          )}

        </div>

        <div className="spn-bottombar">
          <div className="spn-slimrow__actions">
            <Button
              variant="danger"
              title={canDelete ? t("session.deleteSession") : t("session.noSession")}
              aria-label={t("session.deleteSession")}
              onClick={() => setConfirmDeleteSession(true)}
              disabled={!canDelete}
            >
              <Trash2 size={14} />
              <span>{t("session.deleteSession")}</span>
            </Button>
            <Button
              variant="primary"
              title={busy ? t("common.saving") : t("session.saveSession")}
              aria-label={t("session.saveSession")}
              onClick={saveTranscript}
              disabled={busy}
            >
              <Save size={14} />
              <span>{t("session.saveSession")}</span>
            </Button>
          </div>
        </div>
      </div>

      {showContextSheet && (
        <>
          <div className="modal-backdrop" onClick={() => setShowContextSheet(false)} />
          <div className="modal-sheet" role="dialog" aria-label={t("session.additionalContext")}>
            <div className="modal-header">
              <strong>{t("session.additionalContext")}</strong>
              <Button variant="ghost" iconOnly className="modal-close" onClick={() => setShowContextSheet(false)} aria-label={t("common.close")}>✕</Button>
            </div>
            <div className="modal-body">
              <SessionContextPanel
                patientId={selectedPatientId || "NO_PATIENT_ID"}
                apiBaseUrl={API}
                getToken={async () => {
                  const t = await getToken();
                  if (!t) throw new Error("Missing auth token for SessionContextPanel");
                  return t;
                }}
                selectedSessionIds={similarSessionIds}
                onChangeSessions={setSimilarSessionIds}
                selectedAssessmentIds={[]}
                onChangeAssessments={() => {}}
              />
            </div>
          </div>
        </>
      )}
    </div>
    <ConfirmDialog
      open={confirmClearTranscript}
      title={t("session.clearTranscriptTitle")}
      message={t("session.clearTranscriptMessage")}
      confirmLabel={t("common.clear")}
      variant="danger"
      onConfirm={() => {
        setConfirmClearTranscript(false);
        void clearTranscript();
      }}
      onCancel={() => setConfirmClearTranscript(false)}
    />
    <ConfirmDialog
      open={confirmDeleteSession}
      title={t("session.deleteSessionTitle")}
      message={t("session.deleteSessionMessage")}
      confirmLabel={t("common.delete")}
      variant="danger"
      onConfirm={() => {
        setConfirmDeleteSession(false);
        void deleteSession();
      }}
      onCancel={() => setConfirmDeleteSession(false)}
    />
    <Dialog
      open={experientialOpen}
      onClose={() => setExperientialOpen(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: "80vh" } }}
    >
      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", height: "100%" }}>
        {selectedPatientId && (
          <ExperientialPanel
            patientId={selectedPatientId}
            sessionId={effectiveSid}
            language={i18n.language as "en" | "hu"}
            getToken={ensureToken}
          />
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
